"use client";

import type { AmmLevel } from "deso-protocol";
import { chunk, sumBy } from "es-toolkit";
import type { ReactNode } from "react";

import type { AMMConfigItem } from "@/app/(app)/trade/launch/new/page";
import { PreviewRowItem } from "@/components/app/app-layout/coin-info-widget";
import CoinPriceFormatter from "@/components/shared/coin-price-formatter";
import {
  BONDING_CURVE_DISPLAY_NAME,
  FOCUS_TOKEN_DISPLAY_NAME,
} from "@/utils/constants";
import { cn } from "@/utils/lib";

const AmmSummary = ({
  orders,
  coinUsername,
  ammConfig,
  renderTotalAmount,
}: {
  orders: AmmLevel[] | null;
  coinUsername: string;
  ammConfig: AMMConfigItem[];
  renderTotalAmount?: () => ReactNode;
}) => {
  if (!orders?.length) {
    return null;
  }

  const firstLevel = orders[0] || [];

  const lastLevel = orders[orders.length - 1] || [];

  const totalTokensInOrderbook = sumBy(orders, (level) => {
    return level.AmountInQuoteCurrency / level.PriceInQuoteCurrency;
  });

  const totalAmountInUsdAtSetup = sumBy(orders, (x) => x.AmountInUsdAtSetup);

  return (
    <div className="my-2 flex flex-col rounded-2xl border">
      <div
        className={cn("grid p-4", {
          "grid-cols-3": renderTotalAmount,
          "grid-cols-2 gap-4 md:grid-cols-4": !renderTotalAmount,
        })}
      >
        <div className="flex w-full flex-col gap-1">
          <div className="text-xs text-muted"># of Levels</div>
          <div className="font-mono text-base text-muted-foreground">
            {orders.length || 0}
          </div>
          <div className="font-mono text-xs text-muted">
            {BONDING_CURVE_DISPLAY_NAME}
          </div>
        </div>
        <div className="flex w-full flex-col gap-1">
          <div className="text-xs text-muted">Start Price</div>
          <div className="text-base text-muted-foreground">
            <CoinPriceFormatter
              price={firstLevel.PriceInUsdAtSetup}
              className="font-mono text-base text-muted-foreground"
            />
          </div>
          <div className="text-xs text-muted">
            <span className="flex font-mono">
              <CoinPriceFormatter
                price={firstLevel.PriceInQuoteCurrency}
                isUsd={false}
                className="mr-1 font-mono"
              />{" "}
              {FOCUS_TOKEN_DISPLAY_NAME}
            </span>
          </div>
        </div>
        <div className="flex w-full flex-col gap-1">
          <div className="text-xs text-muted">Last Price</div>
          <div className="text-base text-muted-foreground">
            <CoinPriceFormatter
              price={lastLevel.PriceInUsdAtSetup}
              className="font-mono text-base text-muted-foreground"
            />
          </div>
          <div className="text-xs text-muted">
            <span className="flex font-mono">
              <CoinPriceFormatter
                price={lastLevel.PriceInQuoteCurrency}
                isUsd={false}
                className="mr-1 font-mono"
              />{" "}
              {FOCUS_TOKEN_DISPLAY_NAME}
            </span>
          </div>
        </div>
        <div className="flex w-full flex-col gap-1">
          {!renderTotalAmount?.() && (
            <>
              <div className="text-xs text-muted">Total Amount in Ask</div>
              <CoinPriceFormatter
                price={totalAmountInUsdAtSetup}
                className="font-mono text-base text-red-500"
              />
              <div className="text-xs text-muted">
                <span className="flex flex-nowrap items-start gap-1 font-mono">
                  <CoinPriceFormatter
                    price={totalTokensInOrderbook}
                    isUsd={false}
                  />

                  <span className="truncate wrap-break-word">
                    ${coinUsername}
                  </span>
                </span>
              </div>
            </>
          )}
        </div>
      </div>
      {renderTotalAmount?.() && (
        <div
          className="
            grid w-full grid-cols-2 gap-6 border-t p-4
            sm:grid-cols-3 sm:gap-0
          "
        >
          {renderTotalAmount?.()}
        </div>
      )}
      <div
        className="
          grid gap-x-4 border-t border-border-light py-1
          sm:grid-cols-2
        "
      >
        {chunk(ammConfig, 4).map((subList, i) => (
          <div key={i}>
            {subList.map((item) => {
              return !item.value || item.enable === false ? (
                <div
                  className={cn(
                    `
                      flex w-full items-start justify-between border-b
                      border-border-light px-3 py-2
                      last:border-none
                    `,
                  )}
                  key={item.key}
                >
                  <div
                    className="
                    flex w-full max-w-[55%] flex-row items-end gap-2
                  "
                  >
                    <div className="flex items-start gap-1 text-xs text-muted">
                      <span className="text-xs text-muted">{item.title}</span>
                    </div>
                  </div>
                  <div
                    className="
                      flex flex-col items-end text-right text-xs text-foreground
                    "
                  >
                    <div
                      className="
                        inline-block text-muted
                        hover:text-foreground
                      "
                    >
                      <div
                        className="
                        inline-block font-mono text-muted-foreground
                      "
                      >
                        &mdash;
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <PreviewRowItem
                  key={item.key}
                  label={item.title}
                  labelClassName="text-muted text-xs"
                  value={
                    item.valueLabel
                      ? `${item.value} ${item.valueLabel}`
                      : item.value
                  }
                />
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
};

export default AmmSummary;
